import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, RefreshCw, Send, TriangleAlert, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/app/api";

const RECORD_STYLE = {
  submitted: "bg-emerald-50 text-emerald-700",
  in_progress: "bg-amber-50 text-amber-700",
  failed: "bg-rose-50 text-rose-700",
};

const ATTACHMENT_LABEL = {
  included: ["Included", "text-emerald-600"],
  missing: ["Missing", "text-rose-600"],
  conditional_not_applicable: ["Not applicable", "text-slatey"],
  not_provided: ["Optional — not provided", "text-slatey"],
};

export function PartnerSubmission({ applicationId, applicationStatus, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [previewResponse, historyResponse] = await Promise.all([
        api.get(`/partner/applications/${applicationId}/submission-preview`),
        api.get(`/partner/applications/${applicationId}/submissions`),
      ]);
      setPreview(previewResponse.data);
      setHistory(historyResponse.data.submissions || []);
    } catch (requestError) {
      // 400 = this opportunity has no partner submission configuration — hide the panel
      if (requestError.response?.status === 400) setHidden(true);
      else toast.error(formatApiErrorDetail(requestError.response?.data?.detail) || "Could not load submission details");
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/partner/applications/${applicationId}/submit`);
      toast.success("Application package submitted to the partner.");
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      if (detail?.missing_fields || detail?.missing_documents) toast.error("Cannot submit yet — the missing items are listed below.");
      else toast.error(formatApiErrorDetail(detail) || "Submission failed.");
    } finally {
      setBusy(false);
      await load();
      onChanged?.();
    }
  };

  if (hidden) return null;
  if (!preview) return null;

  const validation = preview.validation;
  const latest = preview.latest_submission;
  const alreadySubmitted = preview.application_status === "submitted_to_partner" && latest?.status !== "failed";
  const canSubmit = validation.ready && !alreadySubmitted && validation.method === "email";
  const showRetry = latest?.status === "failed" && !alreadySubmitted;

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5" data-testid="partner-submission">
      <h2 className="font-display text-xl font-bold text-ink">Submit to partner</h2>

      <div className="mt-3 rounded-2xl bg-surface p-3 text-xs text-slatey">
        <p><span className="font-bold text-ink">Method:</span> <span className="capitalize" data-testid="submission-method">{validation.method}</span>
          {validation.config_version != null && <span> · config v{validation.config_version}</span>}</p>
        {validation.recipient && <p className="mt-1"><span className="font-bold text-ink">Recipient:</span> <span data-testid="submission-recipient">{validation.recipient}</span></p>}
        {validation.subject && <p className="mt-1"><span className="font-bold text-ink">Subject:</span> <span data-testid="submission-subject">{validation.subject}</span></p>}
      </div>

      {validation.body_fields?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold uppercase text-slatey">Email body fields</p>
          <ul className="mt-2 space-y-1.5">
            {validation.body_fields.map((field) => (
              <li key={field.key} className="flex items-start justify-between gap-3 text-sm" data-testid={`submission-field-${field.key}`}>
                <span className="capitalize text-slatey">{field.label}</span>
                {field.present
                  ? <span className="max-w-[55%] truncate text-right font-semibold text-ink" title={field.value}>{field.value}</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-bold text-rose-600"><X className="h-3.5 w-3.5" /> Missing</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {validation.attachments?.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold uppercase text-slatey">Attachment checklist</p>
          <ul className="mt-2 space-y-1.5">
            {validation.attachments.map((attachment) => {
              const [label, colour] = ATTACHMENT_LABEL[attachment.status] || [attachment.status, "text-slatey"];
              return (
                <li key={attachment.document} className="flex items-start justify-between gap-3 text-sm" data-testid={`submission-attachment-${attachment.document}`}>
                  <span className="text-slatey">
                    <span className="capitalize">{attachment.document.replaceAll("_", " ")}</span>
                    {attachment.condition && attachment.status === "conditional_not_applicable" && (
                      <span className="block text-xs">Condition: {attachment.condition}</span>
                    )}
                  </span>
                  <span className={`inline-flex shrink-0 items-center gap-1 text-xs font-bold ${colour}`}>
                    {attachment.status === "included" ? <Check className="h-3.5 w-3.5" /> : attachment.status === "missing" ? <X className="h-3.5 w-3.5" /> : null}
                    {attachment.document_name && attachment.status === "included" ? attachment.document_name : label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!validation.ready && (
        <div className="mt-4 rounded-2xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="submission-missing-summary">
          <p className="flex items-center gap-1.5 font-bold"><TriangleAlert className="h-3.5 w-3.5" /> Cannot submit yet</p>
          {validation.missing_fields?.length > 0 && <p className="mt-1">Missing information: {validation.missing_fields.map((f) => f.replaceAll("_", " ")).join(", ")}</p>}
          {validation.missing_documents?.length > 0 && <p className="mt-1">Missing documents: {validation.missing_documents.map((d) => d.replaceAll("_", " ")).join(", ")}</p>}
        </div>
      )}

      {validation.method !== "email" && (
        <p className="mt-4 rounded-2xl bg-surface p-3 text-xs text-slatey" data-testid="submission-manual-note">
          This partner uses a {validation.method} submission method.{validation.instructions ? ` ${validation.instructions}` : " Submit via the partner's own process."}
        </p>
      )}

      {alreadySubmitted ? (
        <p className="mt-4 rounded-2xl bg-emerald-50 p-3 text-xs font-bold text-emerald-700" data-testid="submission-complete-note">
          Submitted to the partner{latest?.submitted_at ? ` on ${latest.submitted_at.slice(0, 16).replace("T", " ")}` : ""}.
        </p>
      ) : validation.method === "email" && (
        <button onClick={submit} disabled={busy || !canSubmit} data-testid="submit-to-partner-button"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : showRetry ? <RefreshCw className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          {showRetry ? "Retry submission" : "Submit application package"}
        </button>
      )}

      {history.length > 0 && (
        <div className="mt-5" data-testid="submission-history">
          <p className="text-xs font-bold uppercase text-slatey">Submission history</p>
          <ul className="mt-2 space-y-2">
            {history.map((record) => (
              <li key={record.id} className="rounded-2xl bg-surface p-3 text-xs" data-testid={`submission-record-${record.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`rounded-full px-2.5 py-1 font-bold capitalize ${RECORD_STYLE[record.status] || "bg-slate-100 text-slatey"}`} data-testid={`submission-record-status-${record.id}`}>
                    {record.status.replaceAll("_", " ")}
                  </span>
                  <span className="text-slatey">{record.created_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <p className="mt-1.5 text-slatey">To {record.recipient} · by {record.submitted_by}</p>
                {record.error && <p className="mt-1 font-semibold text-rose-600" data-testid={`submission-record-error-${record.id}`}>{record.error}</p>}
                {record.status === "failed" && !alreadySubmitted && (
                  <button onClick={submit} disabled={busy || !validation.ready} data-testid={`submission-retry-${record.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 font-bold text-ink disabled:opacity-50">
                    <RefreshCw className="h-3 w-3" /> Retry
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
